import React, { useState, useEffect, useRef } from 'react';
import { generateTestCases } from '../services/api';

const DEFAULT_COLUMNS = [
  { name: 'Title', desc: 'Format: "<Feature>: Verify <Condition/Action>"' },
  { name: 'Priority', desc: 'P0-Critical, P1-High, P2-Medium, P3-Low' },
  { name: 'Type', desc: 'Smoke, Sanity, Regression, Integration' },
  { name: 'Preconditions', desc: 'Setup or state needed before execution' },
  { name: 'Steps', desc: 'Numbered step-by-step instructions' },
  { name: 'Expected Result', desc: 'Numbered results matching each step' },
  { name: 'Locale', desc: 'US, UK, CA, Flooid (default: US)' },
  { name: 'Brand', desc: 'WFM, AGR, Kobe (default: WFM)' },
  { name: 'Area', desc: 'Functional or Non-Functional' },
  { name: 'Automation Status', desc: 'Not Automated, Automation Candidate, Automated, Not Automatable' },
  { name: 'Testcase Subtype', desc: 'Positive, Negative, Edge, End to End' },
  { name: 'Test Type', desc: 'Same as Type field' },
  { name: 'Module', desc: 'Payments, Receipt, Promotion, Tax, Price, UI, Item, etc.' },
  { name: 'Touch Point', desc: 'POS, SCO, Dashcart, QSR, Price checker' }
];

const MODEL_LABELS = {
  'us.anthropic.claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'us.anthropic.claude-opus-4-5-20251101-v1:0': 'Claude Opus 4.5',
  'us.anthropic.claude-haiku-4-5-20251001-v1:0': 'Claude Haiku 4.5'
};

function getModelLabel(id) {
  return MODEL_LABELS[id] || id;
}

function computeOverlapStats(testCases, modelSummary) {
  if (!testCases || !modelSummary) return null;

  // Build a map: normalized title -> set of models that generated it (before dedup)
  // Since backend deduplicates and keeps _source of the first occurrence,
  // we can only know which model "won" each case. But the raw counts from
  // modelSummary tell us how many each model produced before dedup.
  const totalRaw = modelSummary.reduce((sum, m) => sum + m.generated, 0);
  const dedupedCount = testCases.length;
  const duplicatesRemoved = totalRaw - dedupedCount;

  // Count how many deduplicated test cases came from each model
  const perModel = {};
  for (const tc of testCases) {
    const src = tc._source || 'unknown';
    perModel[src] = (perModel[src] || 0) + 1;
  }

  return { totalRaw, dedupedCount, duplicatesRemoved, perModel };
}

function GenerateTestCasesDialog({ isOpen, onClose, onTestCasesGenerated, onUserStoryCapture }) {
  const [userStory, setUserStory] = useState('');
  const [showDefaults, setShowDefaults] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [modelSummary, setModelSummary] = useState(null);
  const [generatedCases, setGeneratedCases] = useState(null);
  const [overlapStats, setOverlapStats] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (generating) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [generating]);

  const handleGenerate = async () => {
    if (!userStory.trim()) return;
    setGenerating(true);
    setError('');
    setModelSummary(null);
    setGeneratedCases(null);
    setOverlapStats(null);
    try {
      const result = await generateTestCases(userStory.trim());
      const summary = result.modelSummary || null;
      const cases = result.testCases || [];
      setModelSummary(summary);
      setGeneratedCases(cases);
      setOverlapStats(computeOverlapStats(cases, summary));
    } catch (err) {
      console.error('Generate test cases error:', err);
      setError(err.message || 'Failed to generate test cases');
    } finally {
      setGenerating(false);
    }
  };

  const handleAccept = () => {
    if (generatedCases) {
      // Capture the user story before resetting so the parent can store it
      if (onUserStoryCapture) onUserStoryCapture(userStory.trim());
      onTestCasesGenerated(generatedCases);
    }
    handleReset();
    onClose();
  };

  const handleReset = () => {
    setModelSummary(null);
    setGeneratedCases(null);
    setOverlapStats(null);
    setUserStory('');
    setShowDefaults(false);
    setError('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  const showSummary = modelSummary && generatedCases;

  return (
    <div className="export-dialog-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-label="Generate Test Cases">
      <div className="export-dialog" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="export-dialog-header">
          <h3>🦅 Hawkeye Test Case Generator</h3>
          <button className="export-dialog-close" onClick={handleClose} aria-label="Close">&times;</button>
        </div>

        {!showSummary ? (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label htmlFor="user-story-input" style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              Enter your User Story
            </label>
            <textarea
              id="user-story-input"
              className="input"
              rows={8}
              value={userStory}
              onChange={e => setUserStory(e.target.value)}
              placeholder={"As a [user], I want to [action], so that [benefit].\n\nAcceptance Criteria:\n- Criterion 1\n- Criterion 2"}
              disabled={generating}
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
            />
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>
              Powered by Amazon Bedrock — queries Claude Sonnet 4.6, Opus 4.5, and Haiku 4.5 in parallel, then merges and deduplicates results for comprehensive coverage.
            </p>
            <div>
              <button
                type="button"
                onClick={() => setShowDefaults(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.83rem', color: 'var(--text-secondary)', padding: 0, fontWeight: 500 }}
              >
                ℹ View default columns
              </button>
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0, fontStyle: 'italic' }}>
              Tip: After generating, use "✨ Refine with AI" on the table to add, remove, or modify columns and test cases.
            </p>
          </div>
        ) : (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16, maxHeight: 'calc(85vh - 140px)', overflowY: 'auto' }}>
            {/* Per-model breakdown */}
            <div>
              <h4 style={{ fontSize: '0.9rem', marginBottom: 10 }}>Per-Model Results</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-secondary)' }}>Model</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: 'var(--text-secondary)' }}>Generated</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: 'var(--text-secondary)' }}>Unique (kept)</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: 'var(--text-secondary)' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {modelSummary.map((m, i) => (
                    <React.Fragment key={i}>
                    <tr style={{ borderBottom: m.error ? 'none' : '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{getModelLabel(m.model)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{m.generated}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        {overlapStats?.perModel[m.model] || 0}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        {m.error ? (
                          <span className="badge badge-danger" title={m.error}>⚠ Failed</span>
                        ) : m.generated > 0 ? (
                          <span className="badge badge-success">✓</span>
                        ) : (
                          <span className="badge badge-warning">No results</span>
                        )}
                      </td>
                    </tr>
                    {m.error && (
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <td colSpan={4} style={{ padding: '4px 12px 8px', fontSize: '0.78rem', color: 'var(--danger)' }}>
                        Error: {m.error}
                      </td>
                    </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Overlap summary */}
            {overlapStats && (
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12
              }}>
                <div style={{
                  background: 'var(--primary-light)', borderRadius: 'var(--radius)',
                  padding: 14, textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
                    {overlapStats.totalRaw}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Total generated</div>
                </div>
                <div style={{
                  background: '#d4edda', borderRadius: 'var(--radius)',
                  padding: 14, textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--success)' }}>
                    {overlapStats.dedupedCount}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Unique (after dedup)</div>
                </div>
                <div style={{
                  background: 'var(--accent-light)', borderRadius: 'var(--radius)',
                  padding: 14, textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent)' }}>
                    {overlapStats.duplicatesRemoved}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Duplicates removed</div>
                </div>
              </div>
            )}
          </div>
        )}

        {error && <div className="error-message" role="alert" style={{ margin: '0 20px 12px' }}>{error}</div>}

        <div className="export-dialog-footer">
          {showSummary ? (
            <>
              <button className="btn btn-secondary" onClick={handleReset}>Generate Again</button>
              <button className="btn btn-primary" onClick={handleAccept}>
                ✓ Use {generatedCases.length} Test Cases
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={handleClose} disabled={generating}>Cancel</button>
              <button className="btn btn-primary" onClick={handleGenerate} disabled={!userStory.trim() || generating}>
                {generating ? `⏳ Querying 3 AI models... ${elapsed}s` : '🚀 Generate Test Cases'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Default columns popup */}
      {showDefaults && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 10001
          }}
          onClick={() => setShowDefaults(false)}
        >
          <div
            style={{
              background: 'var(--card-bg, #fff)', borderRadius: 'var(--radius, 8px)',
              padding: '20px 24px', maxWidth: 560, width: '90%', maxHeight: '80vh',
              overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h4 style={{ margin: 0, fontSize: '1rem' }}>Default Output Columns (14)</h4>
              <button
                onClick={() => setShowDefaults(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-secondary)' }}
                aria-label="Close"
              >&times;</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border, #ddd)' }}>
                  <th style={{ textAlign: 'center', padding: '6px 8px', width: 36, color: 'var(--text-secondary)' }}>#</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)' }}>Column</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)' }}>Values / Format</th>
                </tr>
              </thead>
              <tbody>
                {DEFAULT_COLUMNS.map((col, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border, #eee)' }}>
                    <td style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text-secondary)' }}>{i + 1}</td>
                    <td style={{ padding: '6px 8px', fontWeight: 500 }}>{col.name}</td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{col.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 12, marginBottom: 0 }}>
              Use "Customize output fields" to add, remove, or update any of these columns.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default GenerateTestCasesDialog;
